import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

export const SubscriptionCard = ({ user, compact = false }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionPrice, setSubscriptionPrice] = useState(49.90);

  useEffect(() => {
    fetchSubscription();
    fetchSubscriptionPrice();
  }, [user]);

  const fetchSubscriptionPrice = async () => {
    try {
      const response = await axiosInstance.get('/system-config');
      setSubscriptionPrice(response.data.subscription_price || 49.90);
    } catch (error) {
      console.error('Erro ao buscar preço:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const userId = user?.id || user?.user_id;
      const response = await axiosInstance.get(`/subscription/status/${userId}`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Erro ao carregar assinatura:', error);
      // Não mostrar toast de erro aqui para não poluir a UI
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!subscription) {
    return null;
  }

  const getStatusInfo = () => {
    switch (subscription.status) {
      case 'trial':
        return {
          icon: <Clock className="w-4 h-4" />,
          label: 'Período Trial',
          color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
          message: `${subscription.days_remaining || 0} dias restantes`,
        };
      case 'active':
        const diasRestantes = subscription.days_remaining || 0;
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'Ativo',
          color: 'bg-green-500/20 text-green-300 border-green-500/50',
          message: diasRestantes > 0 
            ? `${diasRestantes} dias para renovar`
            : 'Assinatura ativa',
        };
      case 'expired':
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: 'Expirado',
          color: 'bg-red-500/20 text-red-300 border-red-500/50',
          message: 'Assine para continuar',
        };
      case 'pending':
        return {
          icon: <Clock className="w-4 h-4" />,
          label: 'Pendente',
          color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
          message: 'Assine abaixo para ativar',
        };
      default:
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: 'Sem Assinatura',
          color: 'bg-gray-500/20 text-gray-300 border-gray-500/50',
          message: 'Clique em Assinatura para ativar',
        };
    }
  };

  const statusInfo = getStatusInfo();
  
  // Verificar se deve mostrar botão de renovar (assinatura ativa com menos de 7 dias)
  const showRenewButton = subscription.status === 'active' && (subscription.days_remaining || 0) <= 7;

  // Modo compacto - apenas badge e mensagem em uma linha
  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="subscription-card-compact">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusInfo.color} border`}>
          {statusInfo.icon}
          <span className="text-xs font-medium">{statusInfo.message}</span>
        </div>
        {(subscription.status === 'trial' || subscription.status === 'expired' || subscription.status === 'pending' || subscription.status === 'cancelled' || !subscription.status) && (
          <Button
            size="sm"
            onClick={() => navigate('/assinatura')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-xs px-3 py-1 h-auto"
            data-testid="subscribe-now-button"
          >
            Assinar - R$ {subscriptionPrice.toFixed(2).replace('.', ',')}/mês
          </Button>
        )}
        {showRenewButton && (
          <Button
            size="sm"
            onClick={() => navigate('/assinatura')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-xs px-3 py-1 h-auto"
            data-testid="renew-button"
          >
            Renovar
          </Button>
        )}
      </div>
    );
  }

  // Modo padrão - card completo
  return (
    <Card className="glass border-white/10 mb-6" data-testid="subscription-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${statusInfo.color.split(' ')[0]}`}>
              {statusInfo.icon}
            </div>
            <div>
              <Badge className={statusInfo.color} data-testid="subscription-status-badge">
                {statusInfo.label}
              </Badge>
              <p className="text-sm text-gray-400 mt-1" data-testid="subscription-message">{statusInfo.message}</p>
            </div>
          </div>

          {(subscription.status === 'trial' || subscription.status === 'expired' || subscription.status === 'pending' || subscription.status === 'cancelled' || !subscription.status) && (
            <Button
              onClick={() => navigate('/assinatura')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="subscribe-now-button"
            >
              Assinar Agora - R$ {subscriptionPrice.toFixed(2).replace('.', ',')}/mês
            </Button>
          )}
          
          {showRenewButton && (
            <Button
              onClick={() => navigate('/assinatura')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              data-testid="renew-button"
            >
              Renovar Assinatura
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};